import React, { useMemo } from 'react';
import { array, bool, node, object, string } from 'prop-types';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks, GetYourGuideWidget, getLocationIdFromAddress } from '../../../components';

import css from './SearchResultsPanel.module.css';

const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings,
    pagination,
    search,
    setActiveListing,
    isMapVariant,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="SearchPage"
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  // Dedupe listing variants for the grid view. Each "main" listing can have
  // multiple location-specific variants representing the same asset. We show
  // them all on the map (as pins) but collapse to one per asset in the grid.
  // Two passes:
  //   1. Match by publicData.mainListingId for proper variant metadata.
  //   2. Fallback: match by title + author when no variant metadata exists.
  //
  // Then re-rank into tiers:
  //   Tier 0: Partner listings (RentalTide-flagged)
  //   Tier 1: Listings whose host has 20+ total bookings
  //   Tier 2: Everything else
  // Stable within each tier so Sharetribe's underlying sort is preserved.
  const gridListings = useMemo(() => {
    const sorted = [...listings].sort((a, b) => {
      const aIsVariant = a?.attributes?.publicData?.isLocationVariant === true ? 1 : 0;
      const bIsVariant = b?.attributes?.publicData?.isLocationVariant === true ? 1 : 0;
      return aIsVariant - bIsVariant;
    });
    const seenCanonicalIds = new Set();
    const seenTitleAuthor = new Set();
    const deduped = sorted.filter(l => {
      const pd = l?.attributes?.publicData || {};
      const isVariant = pd.isLocationVariant === true;
      const canonicalId = isVariant ? pd.mainListingId : l?.id?.uuid;
      if (canonicalId) {
        if (seenCanonicalIds.has(canonicalId)) return false;
        seenCanonicalIds.add(canonicalId);
      }
      const title = (l?.attributes?.title || '').trim().toLowerCase();
      const authorId =
        l?.author?.id?.uuid ||
        l?.relationships?.author?.data?.id?.uuid ||
        '';
      if (title && authorId) {
        const titleAuthorKey = `${authorId}::${title}`;
        if (seenTitleAuthor.has(titleAuthorKey)) return false;
        seenTitleAuthor.add(titleAuthorKey);
      }
      return true;
    });

    // Only apply tier ranking when the user hasn't explicitly chosen a sort.
    // If they picked "lowest price", "newest", etc., respect Sharetribe's order.
    const userSort = search?.sort;
    const isDefaultSort = !userSort || userSort === '' || userSort === 'relevance';
    if (!isDefaultSort) {
      return deduped;
    }

    const tierFor = l => {
      const pd = l?.attributes?.publicData || {};
      // Promoted = host paid to boost their own listing. Always at the top.
      if (pd.promoteCommission === true) return 0;
      // Partner = platform-level integration (RentalTide etc.).
      if (pd.isRentalTideListing === true || pd.partner === true) return 1;
      const hostBookings =
        Number(l?.author?.attributes?.profile?.metadata?.hostBookingCount) || 0;
      if (hostBookings >= 20) return 2;
      return 3;
    };
    return deduped
      .map((l, originalIndex) => ({ l, tier: tierFor(l), originalIndex }))
      .sort((a, b) => a.tier - b.tier || a.originalIndex - b.originalIndex)
      .map(x => x.l);
  }, [listings, search]);

  // Get the address from search params for the GYG widget
  const searchAddress = search?.address || '';
  const hasGygLocation = getLocationIdFromAddress(searchAddress);

  // Calculate a stable random position for the GYG widget based on search address
  const gygPosition = useMemo(() => {
    if (!hasGygLocation || gridListings.length < 2) return -1;
    // Use address string to generate a stable "random" position
    const hash = searchAddress.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return 1 + (hash % Math.max(1, gridListings.length - 1)); // Between index 1 and second-to-last
  }, [searchAddress, gridListings.length, hasGygLocation]);

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {gridListings.map((l, index) => {
          const listingCard = (
            <ListingCard
              className={css.listingCard}
              key={l.id.uuid}
              listing={l}
              renderSizes={cardRenderSizes(isMapVariant)}
              setActiveListing={setActiveListing}
            />
          );

          // Insert GYG widget at the calculated position
          if (index === gygPosition) {
            return (
              <React.Fragment key={l.id.uuid}>
                <GetYourGuideWidget className={css.gygWidget} searchAddress={searchAddress} />
                {listingCard}
              </React.Fragment>
            );
          }

          return listingCard;
        })}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

SearchResultsPanel.defaultProps = {
  children: null,
  className: null,
  listings: [],
  pagination: null,
  rootClassName: null,
  search: null,
  isMapVariant: true,
};

SearchResultsPanel.propTypes = {
  children: node,
  className: string,
  listings: array,
  pagination: propTypes.pagination,
  rootClassName: string,
  search: object,
  isMapVariant: bool,
};

export default SearchResultsPanel;
